import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { ImageUpload } from "./ImageUpload";
import { SeriesEmblem } from "../SeriesEmblem";

const inputCls = "w-full bg-[#070709] border border-[#232530] px-4 py-2.5 text-[#ede8e1] font-light placeholder:text-[#57534e] focus:border-[#581313] focus:outline-none transition-colors text-sm";

const FIELDS = [
  { key: "site_title", label: "Site Title", testid: "cms-setting-site-title" },
  { key: "owner_name", label: "Your Name / Alias", testid: "cms-setting-owner-name" },
  { key: "tagline", label: "Tagline", testid: "cms-setting-tagline" },
  { key: "location_label", label: "Location Label", testid: "cms-setting-location" },
  { key: "marquee", label: "Marquee Text", testid: "cms-setting-marquee" },
  { key: "dedication", label: "Colophon Dedication — a name, a city, a short vow", testid: "cms-setting-dedication" },
];

const EMBLEMS = [
  { value: "", label: "Auto" },
  { value: "0", label: "Pointed arch" },
  { value: "1", label: "Thorn branch" },
  { value: "2", label: "Vesica piscis" },
];

export const SettingsTab = ({ onSaved }) => {
  const [form, setForm] = useState(null);
  const [subs, setSubs] = useState([]);
  const [series, setSeries] = useState([]);
  const [busy, setBusy] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);

  useEffect(() => {
    api.get("/site").then(({ data }) => setForm(data.settings || {}))
      .catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/subscribers").then(({ data }) => setSubs(data)).catch(() => {});
    api.get("/series").then(({ data }) => setSeries(data)).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/admin/settings", form);
      toast.success("The Vault remembers.");
      onSaved?.().catch(() => {});
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) {
      toast.error("The new passwords do not match.");
      return;
    }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pw.current_password, new_password: pw.new_password,
      });
      toast.success("Password changed. Use it from your next visit.");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPwBusy(false);
    }
  };

  const setEmblem = async (name, emblem) => {
    try {
      await api.put("/admin/series-emblem", { name, emblem });
      setSeries((prev) => prev.map((s) => s.name === name ? { ...s, emblem } : s));
      toast.success(emblem ? `Emblem bound to ${name}.` : `${name} returns to its birth emblem.`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!form) return <p className="font-mono text-xs tracking-[0.3em] uppercase text-[#78716c] flicker">Reading the ledger…</p>;

  const activeSubs = subs.filter((s) => s.active);

  return (
    <div className="space-y-10 max-w-3xl">
      <form onSubmit={save} className="border border-[#232530] bg-[#0d0e12] p-6 sm:p-8 space-y-5" data-testid="cms-settings-form">
        <h2 className="font-serif text-2xl text-[#ede8e1]">Site Ledger</h2>
        {FIELDS.map((f) => (
          <div key={f.key}>
            <label className="block font-mono text-[10px] tracking-[0.25em] uppercase text-[#78716c] mb-2">{f.label}</label>
            <input value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
              className={inputCls} data-testid={f.testid} />
          </div>
        ))}
        <div>
          <label className="block font-mono text-[10px] tracking-[0.25em] uppercase text-[#78716c] mb-2">Portrait Image</label>
          <ImageUpload value={form.portrait_url || ""}
            onChange={(v) => setForm({ ...form, portrait_url: v })} testid="cms-setting-portrait" />
        </div>
        <div>
          <label className="block font-mono text-[10px] tracking-[0.25em] uppercase text-[#78716c] mb-2">Bio / Manifesto</label>
          <textarea rows={4} value={form.bio || ""} onChange={(e) => setForm({ ...form, bio: e.target.value })}
            className={`${inputCls} resize-none`} data-testid="cms-setting-bio" />
        </div>
        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#57534e]">
          The contact form delivers to the owner email set in the server environment (OWNER_EMAIL).
        </p>
        <button type="submit" disabled={busy} data-testid="cms-save-settings-button"
          className="bg-[#581313] hover:bg-[#6b1818] disabled:opacity-50 text-[#ede8e1] border border-[#581313]/60 font-mono text-xs uppercase tracking-widest px-8 py-3 transition-colors">
          {busy ? "Sealing…" : "Save Ledger"}
        </button>
      </form>

      {series.length > 0 && (
        <div className="border border-[#232530] bg-[#0d0e12] p-6 sm:p-8" data-testid="cms-series-emblems">
          <h2 className="font-serif text-2xl text-[#ede8e1] mb-2">Series Emblems</h2>
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#78716c] mb-6">
            Bind a chosen emblem to each series — or leave it to its birth emblem
          </p>
          <div className="divide-y divide-[#14151b]">
            {series.map((s) => (
              <div key={s.name} className="flex items-center gap-4 py-4" data-testid={`emblem-row-${s.name.replace(/\s+/g, "-").toLowerCase()}`}>
                <SeriesEmblem name={s.name} emblem={s.emblem} size={44} />
                <div className="flex-1 min-w-0">
                  <p className="font-serif text-lg text-[#ede8e1] truncate">{s.name}</p>
                  <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#78716c]">{s.chapters.length} chapter{s.chapters.length === 1 ? "" : "s"}</p>
                </div>
                <select value={s.emblem || ""} onChange={(e) => setEmblem(s.name, e.target.value)}
                  className={`${inputCls} w-44`} data-testid={`emblem-select-${s.name.replace(/\s+/g, "-").toLowerCase()}`}>
                  {EMBLEMS.map((em) => <option key={em.value} value={em.value}>{em.label}</option>)}
                </select>
              </div>
            ))}
          </div>
        </div>
      )}

      <form onSubmit={changePassword} className="border border-[#232530] bg-[#0d0e12] p-6 sm:p-8 space-y-5" data-testid="cms-password-form">
        <h2 className="font-serif text-2xl text-[#ede8e1]">Change the Sanctum Key</h2>
        <input type="password" required value={pw.current_password}
          onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
          placeholder="Current password" className={inputCls} data-testid="cms-current-password-input" />
        <div className="grid sm:grid-cols-2 gap-5">
          <input type="password" required minLength={8} value={pw.new_password}
            onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
            placeholder="New password (8+ characters)" className={inputCls} data-testid="cms-new-password-input" />
          <input type="password" required value={pw.confirm}
            onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
            placeholder="Repeat new password" className={inputCls} data-testid="cms-confirm-password-input" />
        </div>
        <button type="submit" disabled={pwBusy} data-testid="cms-change-password-button"
          className="bg-[#581313] hover:bg-[#6b1818] disabled:opacity-50 text-[#ede8e1] border border-[#581313]/60 font-mono text-xs uppercase tracking-widest px-8 py-3 transition-colors">
          {pwBusy ? "Reforging…" : "Change Password"}
        </button>
      </form>

      <div className="border border-[#232530] bg-[#0d0e12] p-6 sm:p-8" data-testid="cms-subscribers-list">
        <h2 className="font-serif text-2xl text-[#ede8e1] mb-2">Subscribers</h2>
        <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#78716c] mb-6">
          {activeSubs.length} souls receive new essays
        </p>
        <div className="divide-y divide-[#14151b]">
          {subs.map((s) => (
            <div key={s.id} className="flex items-center justify-between py-3" data-testid={`subscriber-${s.id}`}>
              <span className="text-[#ede8e1] font-light">{s.email}</span>
              <span className={`font-mono text-[10px] tracking-[0.2em] uppercase ${s.active ? "text-[#581313]" : "text-[#57534e]"}`}>
                {s.active ? "subscribed" : "departed"}
              </span>
            </div>
          ))}
          {subs.length === 0 && (
            <p className="py-8 text-center font-mono text-xs tracking-[0.3em] uppercase text-[#57534e]">No one has inscribed themselves yet.</p>
          )}
        </div>
      </div>
    </div>
  );
};
